import type {
  AuthRoleContent,
  BusinessType,
  OwnerType,
  CompanyDocumentKey,
  DriverDocumentKey,
  UserRole,
} from "../types/vtc";

/** Links per account type; the copy is in t.auth.roles. */
export const AUTH_CONTENT: Record<UserRole, AuthRoleContent> = {
  driver: {
    signupHref: "/signup",
    dashboardHref: "/driver",
  },
  company: {
    signupHref: "/partners/signup",
    dashboardHref: "/company",
  },
};

/** Reads ?role= from the URL, defaulting to driver. */
export function parseRole(value: string | null): UserRole {
  return value === "company" ? "company" : "driver";
}

/** Login URL for a role, keeping the driver one clean. */
export function loginHref(role: UserRole): string {
  return role === "driver" ? "/login" : `/login?role=${role}`;
}

/** Files checked by the platform before a company can publish; labels in t.auth.documents. */
export const OWNER_TYPES: OwnerType[] = ["individual", "company"];

/** Business types a company account can pick; individuals are always "individual-rental". */
export const COMPANY_BUSINESS_TYPES: BusinessType[] = ["vtc-rental", "rental-company"];

/** Documents each business type uploads to get verified (same lists as the backend). */
export const REQUIRED_COMPANY_DOCS: Record<BusinessType, CompanyDocumentKey[]> = {
  "individual-rental": ["idDocument", "vehicleRegistration", "vehicleInsurance"],
  "rental-company": ["kbis", "insurance"],
  "vtc-rental": ["kbis", "insurance", "licence"],
};

export const COMPANY_DOCUMENTS: CompanyDocumentKey[] = ["kbis", "insurance", "licence"];

/** Files a driver uploads from "My documents"; labels in t.auth.documents. */
export const DRIVER_DOCUMENTS: DriverDocumentKey[] = [
  "idDocument",
  "drivingLicence",
  "vtcCard",
  "proofOfAddress",
];

export const DOCUMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png";
export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/** ?next= return path after login/signup; only same-site paths are accepted. */
export function safeNext(value: string | null): string | null {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : null;
}

/** Appends ?next= to an auth link so the user comes back afterwards. */
export function withNext(href: string, next: string | null): string {
  if (!next) return href;
  return `${href}${href.includes("?") ? "&" : "?"}next=${encodeURIComponent(next)}`;
}
